import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  addAdult,
  personalLink,
  PersonalLink,
  PublicStay,
  removeAdult,
  StayConsentWords,
  StayGuestPolicy,
  StayIdentity,
  StayPartyMember,
  StayPublicError,
} from '../../../api/stays-public-client';
import { formatDayKey, isDayKey, zoneLabel } from '../../../function-utils/building-time';
import { StatusLabel } from '../../../motion';
import { StatusPill, StatusTone } from '../../status-pill/status-pill';
import { Stepper } from '../../stepper/stepper';
import { LinkField, LinkRow } from '../invite/invite-qr-modal';
import { LogoMark } from '../../logo/logo-mark';
import {
  BrandRow,
  Card,
  ErrorText,
  Field,
  FieldError,
  FieldHint,
  FieldSkeleton,
  Heading,
  HeadingSkeleton,
  Input,
  Label,
  Shell,
  SkeletonGroup,
  Wordmark,
} from '../onboarding.styled';
import {
  Actions,
  AddBox,
  Body,
  CheckRow,
  Fact,
  Facts,
  Ghost,
  GhostLink,
  LinkBox,
  PartyActions,
  PartyHead,
  PartyItem,
  PartyList,
  PartyName,
  Primary,
  Prose,
  Quiet,
  Secondary,
  SecondaryLink,
  StepperLabel,
  StepperRow,
  SubTitle,
} from './public-stay-page.styled';
import { errorWords, StayKey, t } from './stay-strings';

/**
 * THE STAY GUEST'S STEP VIEWS (plan S3) — shared by the lead guest's page
 * (/s/:token) and one adult's own page (/sg/:token).
 *
 * The token is the whole credential. It is never logged, never put in an href
 * and never sent anywhere but the API; the one place it is kept is
 * sessionStorage['lobbero.stay.return'], written just before the hop to the ID
 * partner so /s/return (which carries no token) can find the stay again.
 *
 * Never shown: a unit number, a door code, the lead's surname, an email, any
 * document field. The server does not send them; nothing here asks.
 */

const L = 'en' as const;

/* ── the page-level helpers ── */

/** Ask the browser to send no Referer from this page while it is open. */
export const useNoReferrer = (): void => {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);

    return () => {
      meta.remove();
    };
  }, []);
};

/** How long the rate-limited page offers no retry: the limiter's own window. */
export const LIMITED_WAIT_MS = 60_000;
/** The return page asks again every 3 s, for up to a minute. */
export const POLL_MS = 3000;
export const POLL_FOR_MS = 60_000;

/** Rate-limited: nothing to press until the limiter's minute has passed. */
export const useLimitedRetry = (): { canRetry: boolean; hold: () => void } => {
  const [canRetry, setCanRetry] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );
  const hold = useCallback(() => {
    setCanRetry(false);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      setCanRetry(true);
    }, LIMITED_WAIT_MS);
  }, []);

  return { canRetry, hold };
};

/** The card every stay guest page sits in: Shell (the one main) + Card + brand. */
export const StayShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Shell>
    <Card>
      <BrandRow>
        <LogoMark size={40} aria-hidden="true" />
        <Wordmark>Lobbero</Wordmark>
      </BrandRow>
      {children}
    </Card>
  </Shell>
);

export const LoadingFill: React.FC = () => (
  <SkeletonGroup label={t(L, 'loadingLabel')}>
    <HeadingSkeleton />
    <FieldSkeleton />
    <FieldSkeleton />
  </SkeletonGroup>
);

/** The four whole-page states, shared by /s and /sg. */
export type PageState = 'invalid' | 'limited' | 'error' | 'suspended';

export const PageStateView: React.FC<{ state: PageState; canRetry: boolean; onRetry: () => void }> = ({
  state,
  canRetry,
  onRetry,
}) => (
  <>
    <Heading>{t(L, state)}</Heading>
    {state === 'error' || (state === 'limited' && canRetry) ? (
      <Actions>
        <Primary type="button" onClick={onRetry}>
          {t(L, 'tryAgain')}
        </Primary>
      </Actions>
    ) : null}
  </>
);

export const RETURN_KEY = 'lobbero.stay.return';
export type ReturnKind = 's' | 'sg';
export interface StayReturn {
  kind: ReturnKind;
  token: string;
}

/** Remember which page to reopen after the ID partner. Storage may be refused. */
export const saveReturn = (kind: ReturnKind, token: string): void => {
  try {
    window.sessionStorage.setItem(RETURN_KEY, JSON.stringify({ kind, token }));
  } catch {
    // Private mode or blocked storage: /s/return then asks for the link again.
  }
};

export const readReturn = (): StayReturn | null => {
  try {
    const raw = window.sessionStorage.getItem(RETURN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StayReturn> | null;
    if (!parsed || (parsed.kind !== 's' && parsed.kind !== 'sg')) return null;
    if (typeof parsed.token !== 'string' || !parsed.token) return null;

    return { kind: parsed.kind, token: parsed.token };
  } catch {
    return null;
  }
};

/** Hand the guest to the ID partner. A function so tests can watch the order. */
export const leaveFor = (url: string): void => {
  window.location.assign(url);
};

/**
 * A building calendar day ('YYYY-MM-DD') as "Tue 6 Oct". formatDayKey prints
 * the key in UTC, so no zone — the device's or the building's — can move it to
 * a neighbouring day (and the one formatter stays in building-time.ts).
 */
export const formatStayDate = (key: string): string =>
  isDayKey(key) ? formatDayKey(key, { weekday: 'short', day: 'numeric', month: 'short' }) : String(key || '');

/** "Times are Lisbon time." — always said: the times are the building's wall clock. */
export const stayZoneNote = (zone: string | null | undefined): string => {
  const city = zoneLabel(zone);
  if (!city || city === 'UTC') return t(L, 'zoneNoteUtc');

  return t(L, 'zoneNote', { city });
};

export const nightsGuestsLine = (nights: number, guests?: number | null): string => {
  const n = t(L, nights === 1 ? 'oneNight' : 'manyNights', { n: nights });
  if (guests == null) return n;
  const g = t(L, guests === 1 ? 'oneGuest' : 'manyGuests', { n: guests });

  return t(L, 'nightsGuests', { nights: n, guests: g });
};

const PILL: Record<StayIdentity, { tone: StatusTone; key: StayKey }> = {
  not_started: { tone: 'neutral', key: 'pillNotStarted' },
  pending: { tone: 'warning', key: 'pillPending' },
  verified: { tone: 'success', key: 'pillVerified' },
  failed: { tone: 'alert', key: 'pillFailed' },
};

export const IdentityPill: React.FC<{ identity: StayIdentity }> = ({ identity }) => {
  const p = PILL[identity] || PILL.not_started;

  return (
    <StatusPill tone={p.tone} dot>
      {t(L, p.key)}
    </StatusPill>
  );
};

/** One h1 per route: the lead's steps are the page title; the one-adult page's are h2. */
export const StepTitle: React.FC<{ level: 1 | 2; children: React.ReactNode }> = ({ level, children }) =>
  level === 1 ? <Heading>{children}</Heading> : <SubTitle>{children}</SubTitle>;

/** What a refusal says inline. The caller may act on the code first. */
export type OnError = (err: unknown) => string | null;

/* ── the stay summary ── */

type StayDates = Pick<PublicStay['stay'], 'check_in' | 'check_out' | 'nights' | 'check_in_time' | 'check_out_time'>;

export const StayFacts: React.FC<{ stay: StayDates; zone: string; guests?: number | null }> = ({
  stay,
  zone,
  guests,
}) => (
  <Facts>
    <Fact data-at={stay.check_in} data-time={stay.check_in_time}>
      {t(L, 'checkInLine', { date: formatStayDate(stay.check_in), time: stay.check_in_time })}
    </Fact>
    <Fact data-at={stay.check_out} data-time={stay.check_out_time}>
      {t(L, 'checkOutLine', { date: formatStayDate(stay.check_out), time: stay.check_out_time })}
    </Fact>
    <Fact>{nightsGuestsLine(stay.nights, guests)}</Fact>
    <Quiet data-clock-note="" data-clock-whose="building">
      {stayZoneNote(zone)}
    </Quiet>
  </Facts>
);

/* ── dates ── */

export interface DatesInput {
  expected_arrival_time?: string | null;
  adults?: number;
}

export const DatesStep: React.FC<{
  stay: PublicStay;
  onConfirm: (input: DatesInput) => Promise<void>;
  onError: OnError;
  notice?: string | null;
}> = ({ stay, onConfirm, onError, notice = null }) => {
  const { policy } = stay;
  const askAdults = policy.who_verifies === 'every_adult' && stay.party_size > 1;
  const askArrival = policy.arrival_time_setter === 'guest_may';
  const [adults, setAdults] = useState<number>(
    Math.min(stay.party_size, Math.max(1, stay.adults ?? stay.party_size))
  );
  const [arrival, setArrival] = useState<string>(stay.stay.expected_arrival_time || '');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setFields({});
    setProblem(null);
    const input: DatesInput = {};
    if (askAdults) input.adults = adults;
    if (askArrival) input.expected_arrival_time = arrival || null;
    try {
      await onConfirm(input);
    } catch (err) {
      setBusy(false);
      if (err instanceof StayPublicError && err.status === 400 && Object.keys(err.fields).length) {
        const known = ['adults', 'expected_arrival_time'];
        setFields(err.fields);
        if (!Object.keys(err.fields).some(k => known.includes(k))) setProblem(errorWords('STAY_GUEST_ERROR'));

        return;
      }
      setProblem(onError(err));
    }
  };

  return (
    <>
      <Heading>{t(L, 'datesTitle')}</Heading>
      <StayFacts stay={stay.stay} zone={stay.building.timezone} guests={stay.party_size} />

      {askAdults ? (
        <StepperRow>
          <StepperLabel id="stay-adults-label">{t(L, 'adultsLabel')}</StepperLabel>
          <Stepper
            value={adults}
            min={1}
            max={stay.party_size}
            onChange={setAdults}
            label={t(L, 'adultsStepperNoun')}
            disabled={busy}
          />
          <FieldError message={fields.adults} />
        </StepperRow>
      ) : null}

      {askArrival ? (
        <Field>
          <Label htmlFor="stay-arrival">{t(L, 'arrivalLabel')}</Label>
          <Input
            id="stay-arrival"
            type="time"
            value={arrival}
            onChange={e => setArrival(e.target.value)}
            aria-describedby="stay-arrival-hint"
            aria-invalid={fields.expected_arrival_time ? true : undefined}
            hasError={!!fields.expected_arrival_time}
            disabled={busy}
          />
          <FieldHint id="stay-arrival-hint">{t(L, 'arrivalHint')}</FieldHint>
          <FieldError message={fields.expected_arrival_time} />
        </Field>
      ) : null}

      {problem || notice ? <ErrorText>{problem || notice}</ErrorText> : null}
      <Actions>
        <Primary type="button" onClick={submit} aria-busy={busy || undefined}>
          <StatusLabel
            state={busy ? 'busy' : 'idle'}
            labels={{ idle: t(L, 'confirmDates'), busy: t(L, 'confirmingDates') }}
          />
        </Primary>
        <Quiet>{t(L, 'datesWrong')}</Quiet>
      </Actions>
    </>
  );
};

/* ── the party (every_adult) ── */

const COPIED_MS = 1800;

const useCopied = () => {
  const [copied, setCopied] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );
  const copy = async (what: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(null), COPIED_MS);
    } catch {
      setCopied(null);
    }
  };

  return { copied, copy };
};

const PersonalLinkBox: React.FC<{ first: string; link: PersonalLink; position: number }> = ({
  first,
  link,
  position,
}) => {
  const { copied, copy } = useCopied();
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const share = async () => {
    try {
      await navigator.share({ text: link.message_text });
    } catch {
      // Dismissed, or the browser refused: the other options are still here.
    }
  };
  const fieldId = `stay-link-${position}`;

  return (
    <LinkBox data-personal-link="">
      <Quiet>{t(L, 'linkOnce', { first })}</Quiet>
      <LinkRow>
        <LinkField
          id={fieldId}
          readOnly
          value={link.url}
          aria-label={t(L, 'linkFor', { first })}
          onFocus={e => e.currentTarget.select()}
        />
        <Secondary type="button" onClick={() => copy('link', link.url)}>
          {copied === 'link' ? t(L, 'copied') : t(L, 'copyLink')}
        </Secondary>
      </LinkRow>
      <Secondary type="button" onClick={() => copy('message', link.message_text)}>
        {copied === 'message' ? t(L, 'copied') : t(L, 'copyMessage')}
      </Secondary>
      <SecondaryLink href={link.whatsapp_url} target="_blank" rel="noopener noreferrer">
        {t(L, 'whatsapp')}
      </SecondaryLink>
      {canShare ? (
        <Secondary type="button" onClick={share}>
          {t(L, 'share')}
        </Secondary>
      ) : null}
      <GhostLink href={link.url}>{t(L, 'openHere')}</GhostLink>
    </LinkBox>
  );
};

const PartyMemberRow: React.FC<{
  member: StayPartyMember;
  token: string;
  onRemoved: () => Promise<void>;
  onLinked: (position: number) => void;
  onError: OnError;
}> = ({ member, token, onRemoved, onLinked, onError }) => {
  const [link, setLink] = useState<PersonalLink | null>(null);
  const [busy, setBusy] = useState<'link' | 'remove' | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const first = member.first_name;
  const verified = member.identity === 'verified';
  const started = member.identity !== 'not_started' || member.consent_given;

  const makeLink = async () => {
    if (busy) return;
    setBusy('link');
    setProblem(null);
    try {
      const out = await personalLink(token, member.position);
      setLink(out);
      onLinked(member.position);
    } catch (err) {
      setProblem(onError(err));
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (busy) return;
    setBusy('remove');
    setProblem(null);
    try {
      await removeAdult(token, member.position);
      await onRemoved();
    } catch (err) {
      setProblem(onError(err));
      setBusy(null);
    }
  };

  return (
    <PartyItem>
      <PartyHead>
        <PartyName>{t(L, 'memberRow', { first, initial: member.last_initial })}</PartyName>
        <IdentityPill identity={member.identity} />
      </PartyHead>
      {!verified ? (
        <PartyActions>
          {link ? (
            <PersonalLinkBox first={first} link={link} position={member.position} />
          ) : (
            <Secondary type="button" onClick={makeLink} aria-busy={busy === 'link' || undefined}>
              <StatusLabel
                state={busy === 'link' ? 'busy' : 'idle'}
                labels={{ idle: t(L, 'sendLink', { first }), busy: t(L, 'makingLink') }}
              />
            </Secondary>
          )}
          {!started ? (
            <Ghost
              type="button"
              onClick={remove}
              aria-label={t(L, 'removeName', { first })}
              aria-busy={busy === 'remove' || undefined}
            >
              <StatusLabel
                state={busy === 'remove' ? 'busy' : 'idle'}
                labels={{ idle: t(L, 'remove'), busy: t(L, 'removing') }}
              />
            </Ghost>
          ) : null}
        </PartyActions>
      ) : null}
      {problem ? <ErrorText>{problem}</ErrorText> : null}
    </PartyItem>
  );
};

const AddAdultForm: React.FC<{
  token: string;
  remaining: number;
  onAdded: (party: StayPartyMember[]) => void;
  onError: OnError;
}> = ({ token, remaining, onAdded, onError }) => {
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setFields({});
    setProblem(null);
    try {
      const out = await addAdult(token, first.trim(), last.trim());
      setFirst('');
      setLast('');
      onAdded(out.party);
    } catch (err) {
      if (err instanceof StayPublicError && err.status === 400 && Object.keys(err.fields).length) {
        setFields(err.fields);
      } else {
        setProblem(onError(err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AddBox onSubmit={submit} noValidate aria-label={t(L, 'addPerson')}>
      <Quiet>{remaining === 1 ? t(L, 'toNameOne') : t(L, 'toNameCount', { n: remaining })}</Quiet>
      <Field>
        <Label htmlFor="stay-add-first">{t(L, 'firstName')}</Label>
        <Input
          id="stay-add-first"
          autoComplete="off"
          value={first}
          onChange={e => setFirst(e.target.value)}
          hasError={!!fields.first_name}
          aria-invalid={fields.first_name ? true : undefined}
        />
        <FieldError message={fields.first_name} />
      </Field>
      <Field>
        <Label htmlFor="stay-add-last">{t(L, 'lastName')}</Label>
        <Input
          id="stay-add-last"
          autoComplete="off"
          value={last}
          onChange={e => setLast(e.target.value)}
          hasError={!!fields.last_name}
          aria-invalid={fields.last_name ? true : undefined}
        />
        <FieldError message={fields.last_name} />
      </Field>
      {problem ? <ErrorText>{problem}</ErrorText> : null}
      <Secondary type="submit" aria-busy={busy || undefined}>
        <StatusLabel state={busy ? 'busy' : 'idle'} labels={{ idle: t(L, 'addPerson'), busy: t(L, 'addingPerson') }} />
      </Secondary>
    </AddBox>
  );
};

/**
 * Who's coming: the lead, every named adult with their ID state, a form for
 * the adults not yet named, and each unverified adult's own link. Used as the
 * party step and, without the Continue button, under "waiting".
 */
export const PartyPanel: React.FC<{
  stay: PublicStay;
  token: string;
  onParty: (party: StayPartyMember[]) => void;
  onRefresh: () => Promise<void>;
  onError: OnError;
}> = ({ stay, token, onParty, onRefresh, onError }) => {
  const party = stay.party || [];
  const lead = party.find(p => p.is_lead);
  const others = party.filter(p => !p.is_lead);
  const adults = Math.max(1, stay.adults ?? 1);
  const remaining = Math.max(0, adults - (others.length + 1));

  const linked = (position: number) =>
    onParty(party.map(p => (p.position === position ? { ...p, personal_link: true } : p)));

  return (
    <>
      <PartyList>
        <PartyItem>
          <PartyHead>
            <PartyName>{t(L, 'leadRow', { first: stay.guest_first_name })}</PartyName>
            <IdentityPill identity={lead ? lead.identity : stay.steps.identity} />
          </PartyHead>
        </PartyItem>
        {others.map(member => (
          <PartyMemberRow
            key={`${member.position}-${member.first_name}`}
            member={member}
            token={token}
            onRemoved={onRefresh}
            onLinked={linked}
            onError={onError}
          />
        ))}
      </PartyList>
      {remaining > 0 ? <AddAdultForm token={token} remaining={remaining} onAdded={onParty} onError={onError} /> : null}
    </>
  );
};

export const PartyStep: React.FC<{
  stay: PublicStay;
  token: string;
  onParty: (party: StayPartyMember[]) => void;
  onRefresh: () => Promise<void>;
  onContinue: () => void;
  onError: OnError;
}> = ({ stay, token, onParty, onRefresh, onContinue, onError }) => {
  const named = (stay.party || []).length;
  const adults = Math.max(1, stay.adults ?? 1);
  const allNamed = named >= adults;

  return (
    <>
      <Heading>{t(L, 'partyTitle')}</Heading>
      <Body>{t(L, 'partySub')}</Body>
      <PartyPanel stay={stay} token={token} onParty={onParty} onRefresh={onRefresh} onError={onError} />
      <Actions>
        {!allNamed ? <Quiet id="stay-party-reason">{t(L, 'everyoneNamed')}</Quiet> : null}
        <Primary
          type="button"
          onClick={onContinue}
          disabled={!allNamed}
          aria-describedby={!allNamed ? 'stay-party-reason' : undefined}
        >
          {t(L, 'continueToId')}
        </Primary>
      </Actions>
    </>
  );
};

/* ── consent ── */

export const ConsentStep: React.FC<{
  consent: StayConsentWords;
  level: 1 | 2;
  onAgree: (selfie: boolean) => Promise<void>;
  onNotNow?: () => void;
  onError: OnError;
  notice?: string | null;
}> = ({ consent, level, onAgree, onNotNow, onError, notice = null }) => {
  const [selfie, setSelfie] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const agree = async () => {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      await onAgree(selfie);
    } catch (err) {
      setBusy(false);
      setProblem(onError(err));
    }
  };

  return (
    <>
      <StepTitle level={level}>{t(L, 'consentTitle')}</StepTitle>
      <Prose>{consent.identity_text}</Prose>
      <Quiet>{t(L, 'privacyVersion', { version: consent.version })}</Quiet>
      <CheckRow htmlFor="stay-selfie">
        <input id="stay-selfie" type="checkbox" checked={selfie} onChange={e => setSelfie(e.target.checked)} />
        <span>{t(L, 'selfieLabel')}</span>
      </CheckRow>
      {consent.selfie_text ? <Quiet>{consent.selfie_text}</Quiet> : null}
      {problem || notice ? <ErrorText>{problem || notice}</ErrorText> : null}
      <Actions>
        <Primary type="button" onClick={agree} aria-busy={busy || undefined}>
          <StatusLabel state={busy ? 'busy' : 'idle'} labels={{ idle: t(L, 'agree'), busy: t(L, 'agreeing') }} />
        </Primary>
        {onNotNow ? (
          <Ghost type="button" onClick={onNotNow}>
            {t(L, 'notNow')}
          </Ghost>
        ) : null}
      </Actions>
    </>
  );
};

/* ── identity ── */

export const IdentityStep: React.FC<{
  identity: StayIdentity;
  attemptsLeft: number;
  level: 1 | 2;
  /** The return page is still waiting to hear from the ID partner. */
  checking?: boolean;
  onStart: () => Promise<void>;
  onError: OnError;
}> = ({ identity, attemptsLeft, level, checking = false, onStart, onError }) => {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const start = async () => {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      // On success the page is leaving for the ID partner: stay "Opening…".
      await onStart();
    } catch (err) {
      setBusy(false);
      setProblem(onError(err));
    }
  };

  if (identity === 'pending' || checking) {
    return (
      <>
        <StepTitle level={level}>{t(L, 'pendingTitle')}</StepTitle>
        <Body role="status">{t(L, 'pending')}</Body>
      </>
    );
  }

  const failed = identity === 'failed';
  if (attemptsLeft <= 0) {
    return (
      <>
        <StepTitle level={level}>{failed ? t(L, 'failedTitle') : t(L, 'identityTitle')}</StepTitle>
        <Body role="status">{t(L, 'exhausted')}</Body>
      </>
    );
  }

  return (
    <>
      <StepTitle level={level}>{failed ? t(L, 'failedTitle') : t(L, 'identityTitle')}</StepTitle>
      <Body>{failed ? t(L, 'failedBody') : t(L, 'identityBody')}</Body>
      {failed ? <Quiet>{t(L, 'identityBody')}</Quiet> : null}
      {problem ? <ErrorText>{problem}</ErrorText> : null}
      <Actions>
        <Primary type="button" onClick={start} aria-busy={busy || undefined}>
          <StatusLabel
            state={busy ? 'busy' : 'idle'}
            labels={{ idle: failed ? t(L, 'tryAgain') : t(L, 'verifyId'), busy: t(L, 'opening') }}
          />
        </Primary>
      </Actions>
    </>
  );
};

/** The policy the page follows — exported so the views never hard-code it. */
export const needsParty = (policy: StayGuestPolicy, adults: number | null): boolean =>
  policy.who_verifies === 'every_adult' && Math.max(1, adults ?? 1) > 1;
